import { type FC, useEffect } from 'react';

import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { useForm } from 'react-hook-form';

import useAttributes from '@/views/AdminDashboard/components/AttributesDrawer/hooks/useAttributes';
import TextFieldActions from '@/views/AdminDashboard/components/TextFieldActions';

interface AttributeTagFrom {
  attributeId: string;
  name: string;
}

interface AttributeTagFormProps {
  attributeId: string;
  isDark?: boolean;
}

const AttributeTagForm: FC<React.PropsWithChildren<AttributeTagFormProps>> = ({
  attributeId,
  isDark,
}) => {
  const { t } = useTranslation();
  const { attributes, onCreateTag, loadingTagPOST } = useAttributes();

  const {
    handleSubmit,
    register,
    reset,
    errors,
    setError,
    clearErrors,
    formState: { isValid },
  } = useForm<AttributeTagFrom>({
    mode: 'onChange',
    defaultValues: { attributeId: attributeId, name: '' },
  });

  useEffect(() => {
    register('attributeId');
  }, [register]);

  // biome-ignore lint/suspicious/useAwait: async signature required by framework
  const onSubmit = async ({ name, attributeId }: AttributeTagFrom) => {
    const tags = name ? name.split(',').map(item => item.trim()) : [];
    for (const label of tags) {
      const attribute = attributes.find(
        attr => attr?.originalId === attributeId,
      );
      const isDuplicated = attribute?.tags?.find(tag => tag?.name === label);
      const isLengthValid = label.length <= 50;

      if ((isDuplicated || !isLengthValid) && tags.length === 1) {
        setError('name', {
          type: isDuplicated ? 'duplicated' : 'length',
          message: isDuplicated
            ? t('ATTR_LABEL_DUPLICATED')
            : t('ATTR_LABEL_LENGTH'),
        });
        return;
      }

      if (!isDuplicated && isLengthValid) {
        onCreateTag({ id: attributeId, name: label });
      }
    }
    reset();
  };

  return (
    <form className="w-full flex mt-12" onSubmit={handleSubmit(onSubmit)}>
      <TextFieldActions
        noCounter
        isDark={isDark}
        name="name"
        hint={t('ATTR_TAG_INPUT_ADD')}
        submitDisabled={!isValid || loadingTagPOST}
        errorText={errors.name?.message}
        onChange={e => e.target.value === '' && clearErrors()}
        ref={register({
          required: {
            value: true,
            message: t('ATTR_INPUT_REQUIRED'),
          },
        })}
      />
    </form>
  );
};

export default AttributeTagForm;
